import { VolumeX } from 'lucide-react'
import { useRecordingStore } from '@/stores/recording'
import { SILENCE_WARNING_DETAIL, SILENCE_WARNING_TEXT } from '@/lib/silence-alert'

/**
 * The 30-minute silence prompt (CLA-1564).
 *
 * Mounted once in AppShell rather than beside the recording controls: a
 * recording left running is usually one nobody is looking at, and the
 * clinician may be anywhere in the app — the patient list, the calendar —
 * when they come back to it. The tab title and (when already permitted) a
 * system notification carry the same warning to a background tab; see
 * `lib/silence-alert.ts`.
 *
 * "Keep recording" re-arms the check, it never turns it off. "Stop & save" is
 * the clinician's own Stop — the normal path, nothing discarded.
 */
export function RecordingSilencePrompt() {
  const silenceWarning = useRecordingStore((s) => s.silenceWarning)
  const state = useRecordingStore((s) => s.state)
  const keepRecording = useRecordingStore((s) => s.keepRecordingThroughSilence)
  const stopRecording = useRecordingStore((s) => s.stopRecording)

  if (!silenceWarning || state !== 'recording') return null

  return (
    <div
      role="alert"
      data-testid="recording-silence-prompt"
      className="shrink-0 bg-warning/15 border-b border-warning/30 px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2"
    >
      <VolumeX className="w-4 h-4 text-warning shrink-0" aria-hidden />
      <div className="flex-1 min-w-[12rem]">
        <p className="text-[13px] font-medium text-ink">{SILENCE_WARNING_TEXT}</p>
        <p className="text-xs text-ink-secondary">{SILENCE_WARNING_DETAIL}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => keepRecording()}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface border border-border text-ink hover:bg-surface-secondary transition-colors"
        >
          Keep recording
        </button>
        <button
          onClick={() => void stopRecording()}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-record text-on-record hover:bg-record-hover transition-colors"
        >
          Stop &amp; save
        </button>
      </div>
    </div>
  )
}
